import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import { Toaster } from 'sonner';
import LandingPage from './pages/LandingPage';
import Dashboard from './pages/Dashboard';
import ModulePage from './pages/ModulePage';
import ReviewPage from './pages/ReviewPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import CoursesPage from './pages/CoursesPage';
import CareerLaunchPage from './pages/CareerLaunchPage';
import TalentReportPage from './pages/TalentReportPage';
import { LogOut } from 'lucide-react';
import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center">
        <div className="text-[#00E5FF] text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-default)', color: 'var(--text-primary)' }}>
      {user && (
        <nav style={{ backgroundColor: 'var(--bg-paper)', borderBottom: '1px solid var(--border-default)' }} className="sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
            <div className="flex items-center gap-6">
              <a href="/dashboard" className="text-xl font-bold tracking-tight" style={{ color: 'var(--primary-main)', fontFamily: 'Outfit, sans-serif' }}>
                Peabsmart DevOps Academy
              </a>
              <div className="flex gap-4">
                <a href="/dashboard" style={{ color: 'var(--text-secondary)' }} className="text-sm hover:text-[var(--text-primary)]">
                  Dashboard
                </a>
                <a href="/career-launch" style={{ color: 'var(--text-secondary)' }} className="text-sm hover:text-[var(--text-primary)]">
                  Career Launch
                </a>
                {(user.role === 'instructor' || user.role === 'teaching_assistant' || user.role === 'admin') && (
                  <a href="/dashboard" style={{ color: 'var(--text-secondary)' }} className="text-sm hover:text-[var(--text-primary)]">
                    Review Queue
                  </a>
                )}
                {user.role === 'admin' && (
                  <a href="/admin/users" style={{ color: 'var(--text-secondary)' }} className="text-sm hover:text-[var(--text-primary)]">
                    Admin
                  </a>
                )}
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-sm">
                <div style={{ color: 'var(--text-primary)' }} className="font-medium">{user.name}</div>
                <div style={{ color: 'var(--text-muted)' }} className="text-xs capitalize">{user.role.replace('_', ' ')}</div>
              </div>
              <button
                onClick={logout}
                className="p-2 rounded-md transition-colors"
                style={{ color: 'var(--text-secondary)' }}
                title="Logout"
              >
                <LogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </nav>
      )}
      {children}
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppLayout>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/courses" element={<CoursesPage />} />
              <Route path="/talent/:slug" element={<TalentReportPage />} />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/modules/:moduleId"
                element={
                  <ProtectedRoute>
                    <ModulePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/career-launch"
                element={
                  <ProtectedRoute>
                    <CareerLaunchPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/review/:submissionId"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'instructor', 'teaching_assistant']}>
                    <ReviewPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <ProtectedRoute allowedRoles={['admin']}>
                    <AdminUsersPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
            <Toaster
              position="top-right"
              theme="dark"
              toastOptions={{
                style: {
                  background: 'var(--bg-paper)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-default)'
                }
              }}
            />
          </AppLayout>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
